/**
 * Persist trip LP solution to InstantDB. Call from main thread only.
 * LP solution is stored as a JSON blob on the trip entity (trips.lpSolution).
 */

import { db } from '../instant';
import type { LpNode, LpEdge, ItemTypeSolution, LpSolveFailureDetail } from './tripLpSolver';
import { solveTripLpInWorker } from './tripLpWorkerClient';
import { buildRequirementsFromPackingLists, type DagNodeRow as DagNodeRowBase } from './tripLpRequirements';
import { LpSolutionSchema } from './dbSchemas';

/** LP solution blob shape: itemTypeId -> { A: NodeQuantity[], T: EdgeFlow[] } */
export type LpSolutionBlob = Record<string, ItemTypeSolution>;

type TripWithLp = { lpSolution?: unknown } | null;

/**
 * Get LP solution from a trip (trips.lpSolution JSON blob).
 * Validates the blob against LpSolutionSchema before returning.
 * Returns null if the trip is null, the solution is absent, or the shape is invalid.
 */
export function getLpSolutionFromTrip(trip: TripWithLp): LpSolutionBlob | null {
    if (!trip) return null;
    const blob = trip.lpSolution;
    if (blob == null) return null;
    const result = LpSolutionSchema.safeParse(blob);
    if (!result.success) {
        console.warn('getLpSolutionFromTrip: lpSolution failed schema validation', result.error.issues);
        return null;
    }
    return result.data as LpSolutionBlob;
}

export type DagNodeRow = DagNodeRowBase;

/** DAG edge from DB */
export interface DagEdgeRow {
    fromNodeIndex: number;
    toNodeIndex: number;
}

export { buildRequirementsFromPackingLists };

export type LpProgressStep = 'solve' | 'lpClear' | 'lpWrite';

/**
 * Rebuild LP solution for a trip: solve, then store in trips.lpSolution (JSON blob).
 */
export async function rebuildTripLpSolution(
    tripId: string,
    dagNodes: DagNodeRow[],
    dagEdges: DagEdgeRow[],
    requirementsByItemType: Record<string, number[]>,
    consumableByItemType?: Record<string, boolean>,
    onProgress?: (step: LpProgressStep) => void,
    bannedEdgesByItemType?: Record<string, Set<string>>
): Promise<{ ok: true; solutions: LpSolutionBlob } | { ok: false; message: string; failure?: LpSolveFailureDetail }> {
    if (dagNodes.length === 0) {
        onProgress?.('lpClear');
        onProgress?.('lpWrite');
        await db.transact([db.tx.trips[tripId].update({ lpSolution: {} })]);
        return { ok: true, solutions: {} };
    }

    const nodes: LpNode[] = dagNodes.map(n => ({ index: n.index, kind: n.kind }));
    const edges: LpEdge[] = dagEdges.map(e => ({ fromNodeIndex: e.fromNodeIndex, toNodeIndex: e.toNodeIndex }));

    const solveInput = {
        nodes,
        edges,
        requirementsByItemType,
        consumableByItemType: consumableByItemType ?? {},
        bannedEdgesByItemType: bannedEdgesByItemType ?? {},
    };

    onProgress?.('solve');
    let result: Awaited<ReturnType<typeof solveTripLpInWorker>>;
    try {
        result = await solveTripLpInWorker(solveInput);
    } catch {
        const { solveTripLp } = await import('./tripLpSolver');
        result = await solveTripLp(solveInput);
    }
    if (!result.ok) return result;

    onProgress?.('lpClear');
    onProgress?.('lpWrite');
    await db.transact([db.tx.trips[tripId].update({ lpSolution: result.solutions })]);
    return { ok: true, solutions: result.solutions };
}

/** Update a single edge flow in the stored solution (for supply flow edit mode). Persists immediately to DB. */
export async function updateTripLpSolutionEdgeFlow(
    tripId: string,
    solution: LpSolutionBlob,
    itemTypeId: string,
    fromNodeIndex: number,
    toNodeIndex: number,
    value: number
): Promise<void> {
    const updated = { ...solution };
    let entry = updated[itemTypeId];
    if (!entry) {
        entry = { A: [], T: [] };
        updated[itemTypeId] = entry;
    }
    const T = [...(entry.T ?? [])];
    const idx = T.findIndex(e => e.fromNodeIndex === fromNodeIndex && e.toNodeIndex === toNodeIndex);
    const newEdge = { fromNodeIndex, toNodeIndex, value };
    if (idx >= 0) T[idx] = newEdge;
    else T.push(newEdge);
    updated[itemTypeId] = { ...entry, T };

    await db.transact([db.tx.trips[tripId].update({ lpSolution: updated })]);
}
